import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { X, Mail, Lock } from 'lucide-react';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { useAuthStore } from '../../store/useAuthStore';
import { useToastStore } from '../../store/useToastStore';
import { supabase } from '../../lib/supabase';

// Export as both default and named export to support both import styles
export function LoginModal({ isOpen, onClose, onSuccess, valuationId }: { 
  isOpen: boolean; 
  onClose: () => void; 
  onSuccess?: () => Promise<void>; 
  valuationId?: string; 
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const { showToast } = useToastStore();
  const { setUser } = useAuthStore();

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await supabase.auth.signInWithEmail(email, password);
      if (response.data.session) {
        const token = response.data.session.access_token;
        setUser({
          id: response.data.user.id,
          user_id: response.data.user.id,
          full_name: response.data.user.user_metadata?.full_name || null,
          name: response.data.user.user_metadata?.name || null,
          email: response.data.user.email || null,
          avatar_url: response.data.user.user_metadata?.avatar_url || null,
          company_name: response.data.user.user_metadata?.company_name || null,
          subscription_tier: 'free',
          subscription_ends_at: null,
          created_at: response.data.user.created_at,
          updated_at: response.data.user.last_sign_in_at || response.data.user.created_at
        }, token);
        
        // Call onSuccess if provided
        if (onSuccess) {
          await onSuccess();
        }
        
        onClose();
      }
    } catch (error: any) {
      console.error('Login error:', error);

      // Handle specific Supabase auth errors
      let errorMessage = 'Failed to sign in';

      if (error.message) {
        if (error.message.includes('Invalid login credentials')) {
          errorMessage = 'Incorrect email or password';
        } else if (error.message.includes('Email not confirmed')) {
          errorMessage = 'Please verify your email address before logging in';
        } else {
          errorMessage = error.message;
        }
      }

      showToast(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    try {
      console.log('LoginModal: Initiating Google OAuth...');

      // Prepare state data for OAuth flow
      const stateData: { redirectPath?: string; valuationId?: string } = {};

      // Set redirect path based on current location
      if (location.pathname.includes('/valuation')) {
        stateData.redirectPath = location.pathname;
        console.log('LoginModal: Setting redirect path:', stateData.redirectPath);
      }

      // Include valuation ID if provided
      if (valuationId) {
        stateData.valuationId = valuationId;
        console.log('LoginModal: Preserving valuation ID:', valuationId);

        // Store as backup in sessionStorage (primary backup method)
        sessionStorage.setItem('pending_valuation_id', valuationId);
      }

      // Pass state data to Google OAuth (will be stored in sessionStorage)
      console.log('LoginModal: Starting Google OAuth with state:', stateData);
      await supabase.auth.signInWithGoogle(stateData);
    } catch (err) {
      console.error('Google login error:', err);
      showToast('Failed to sign in with Google', 'error');
    }
  };

  const handleSignUpClick = () => {
    navigate('/signup', { state: { returnTo: '/valuation' } });
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Sign In to View Results" size="md">
      <div className="space-y-8">
        <form onSubmit={handleLogin} className="space-y-6">
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
              Email Address
            </label>
            <div className="relative">
              <Mail className="absolute left-3 top-3 text-gray-400 w-5 h-5" />
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Enter your email"
                required
              />
            </div>
          </div>

          <div>
            <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
              Password
            </label>
            <div className="relative">
              <Lock className="absolute left-3 top-3 text-gray-400 w-5 h-5" />
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Enter your password"
                required
              />
            </div>
          </div>

          <Button
            type="submit"
            className="w-full"
            loading={loading}
          >
            Sign In with Email
          </Button>
        </form>

        <div className="relative">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-gray-300" />
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="px-2 bg-white text-gray-500">Or continue with</span>
          </div>
        </div>

        <Button
          type="button"
          variant="outline"
          className="w-full"
          onClick={handleGoogleLogin}
        >
          <img
            src="https://www.google.com/favicon.ico"
            alt="Google"
            className="w-5 h-5 mr-2"
          />
          Sign in with Google
        </Button>

        <div className="text-center">
          <p className="text-sm text-gray-600">
            Don't have an account?{' '}
            <button
              onClick={handleSignUpClick}
              className="text-blue-600 hover:text-blue-700 font-medium"
            >
              Sign up
            </button>
          </p>
        </div>
      </div>
    </Modal>
  );
}

// Also export as default for flexibility
export default LoginModal;
